import { useEffect, useRef, useState } from "react";
import Menu from "../../../componentes/Menu/Menu";
import api from "../../../servidor/api";
import Swal from "sweetalert2";
import { Upload, Save, RefreshCw, CheckCircle, XCircle } from "lucide-react";

const PLATAFORMAS = ["android", "ios"];

const POLITICA_PADRAO = {
  plataforma: "android",
  min_build: "",
  store_url: "",
  mensagem: "Nova versão disponível. Atualize o aplicativo para continuar.",
  ativo: "N",
};

export default function MobileVersao() {
  const [politicas, setPoliticas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [salvando, setSalvando] = useState(null); // plataforma sendo salva
  const [uploadInfo, setUploadInfo] = useState(null);
  const [uploadando, setUploadando] = useState(false);
  const inputApkRef = useRef(null);

  async function carregarPoliticas({ silencioso = false } = {}) {
    if (!silencioso) setLoading(true);
    try {
      const { data } = await api.get("/v1/mobile/versao/politicas");
      const lista = Array.isArray(data) ? data : [];

      // Garante uma entrada para cada plataforma conhecida
      const mapa = Object.fromEntries(lista.map((p) => [p.plataforma?.toLowerCase(), p]));
      const resultado = PLATAFORMAS.map((pl) => mapa[pl] || { ...POLITICA_PADRAO, plataforma: pl });
      setPoliticas(resultado);
    } catch {
      Swal.fire({ icon: "error", title: "Erro", text: "Não foi possível carregar as políticas de atualização." });
    } finally {
      if (!silencioso) setLoading(false);
    }
  }

  useEffect(() => {
    carregarPoliticas();
  }, []);

  function atualizarCampo(plataforma, campo, valor) {
    setPoliticas((prev) =>
      prev.map((p) => (p.plataforma === plataforma ? { ...p, [campo]: valor } : p))
    );
  }

  async function salvar(politica) {
    setSalvando(politica.plataforma);
    try {
      await api.post("/v1/mobile/versao/salvar", {
        plataforma: politica.plataforma,
        min_build: politica.min_build,
        store_url: politica.store_url,
        mensagem: politica.mensagem,
        ativo: politica.ativo,
      });
      await Swal.fire({
        icon: "success",
        title: "Salvo!",
        text: `Política para ${politica.plataforma} atualizada com sucesso.`,
        timer: 2000,
        showConfirmButton: false,
      });
      carregarPoliticas({ silencioso: true });
    } catch (error) {
      Swal.fire({ icon: "error", title: "Erro ao salvar", text: error?.response?.data?.error || "Tente novamente." });
    } finally {
      setSalvando(null);
    }
  }

  async function handleUploadApk(event) {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith(".apk")) {
      Swal.fire({ icon: "warning", title: "Arquivo inválido", text: "Selecione um arquivo .apk" });
      return;
    }

    // Pede o versionCode e a mensagem antes de confirmar
    const { value: formValues, isConfirmed } = await Swal.fire({
      title: "Enviar novo APK",
      html: `
        <p style="text-align:left;font-size:13px;margin-bottom:12px">
          Arquivo: <strong>${file.name}</strong> (${(file.size / 1024 / 1024).toFixed(1)} MB)<br/>
          O APK anterior será substituído e a política Android será ativada automaticamente.
        </p>
        <div style="text-align:left;margin-bottom:8px">
          <label style="font-size:13px;font-weight:600">Build mínimo exigido (versionCode)</label>
          <input id="swal-min-build" type="number" min="1" class="swal2-input" placeholder="Ex: 3" style="margin:4px 0 0 0;width:100%">
        </div>
        <div style="text-align:left">
          <label style="font-size:13px;font-weight:600">Mensagem exibida no app</label>
          <textarea id="swal-mensagem" class="swal2-textarea" rows="2" style="margin:4px 0 0 0;width:100%;resize:vertical">Nova versão disponível. Atualize o aplicativo para continuar.</textarea>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: "Enviar APK",
      cancelButtonText: "Cancelar",
      reverseButtons: true,
      preConfirm: () => {
        const min_build = document.getElementById("swal-min-build").value.trim();
        const mensagem = document.getElementById("swal-mensagem").value.trim();
        if (!min_build) {
          Swal.showValidationMessage("Informe o build mínimo (versionCode)");
          return false;
        }
        return { min_build, mensagem };
      },
    });

    if (!isConfirmed || !formValues) {
      event.target.value = "";
      return;
    }

    setUploadando(true);
    setUploadInfo(null);

    try {
      const formData = new FormData();
      formData.append("apk", file);
      formData.append("min_build", formValues.min_build);
      formData.append("mensagem", formValues.mensagem);

      const { data } = await api.post("/v1/mobile/versao/upload-apk", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setUploadInfo(data);

      // Atualiza estado local das políticas com os valores salvos
      setPoliticas((prev) =>
        prev.map((p) =>
          p.plataforma === "android"
            ? { ...p, store_url: data.url_apk, min_build: formValues.min_build, mensagem: formValues.mensagem, ativo: "S" }
            : p
        )
      );

      await Swal.fire({
        icon: "success",
        title: "APK enviado e política ativada!",
        html: `
          <p>A política Android foi atualizada automaticamente:</p>
          <ul style="text-align:left;font-size:13px">
            <li>Build mínimo: <strong>${formValues.min_build}</strong></li>
            <li>Alerta de atualização: <strong>Ativo</strong></li>
            <li>URL: <code style="font-size:11px;word-break:break-all">${data.url_apk}</code></li>
          </ul>
        `,
      });

      carregarPoliticas({ silencioso: true });
    } catch (error) {
      Swal.fire({ icon: "error", title: "Erro no upload", text: error?.response?.data?.error || "Tente novamente." });
    } finally {
      setUploadando(false);
      event.target.value = "";
    }
  }

  return (
    <>
      <Menu />
      <div className="container-fluid Containe-Tela">
        <div className="row text-body-secondary mb-3">
          <h1 className="mb-2 titulo-da-pagina">Configuração - Versão do App Mobile</h1>
          <p className="text-muted mb-0">
            Gerencie as versões mínimas exigidas e o APK disponível para download. Quando o build instalado no dispositivo for menor que o mínimo exigido, o app bloqueará o uso e exibirá a tela de atualização obrigatória com o link para baixar a nova versão.
          </p>
        </div>

        {/* Upload do APK */}
        <div className="card mb-4" style={{ border: "1px solid #e2e8f0" }}>
          <div className="card-body">
            <h5 className="card-title mb-1" style={{ fontWeight: 700 }}>
              Enviar novo APK (Android)
            </h5>
            <p className="text-muted small mb-3">
              Faça o upload do arquivo <code>app-release.apk</code> gerado pelo build. O arquivo será disponibilizado automaticamente em <code>/download/app</code> e a política Android será atualizada no banco de dados — sem etapas adicionais.
            </p>

            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <input
                ref={inputApkRef}
                type="file"
                accept=".apk,application/vnd.android.package-archive"
                style={{ display: "none" }}
                onChange={handleUploadApk}
              />
              <button
                type="button"
                className="btn btn-primary btn-sm"
                disabled={uploadando}
                onClick={() => inputApkRef.current?.click()}
                style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
              >
                <Upload size={14} strokeWidth={2} />
                {uploadando ? "Enviando..." : "Selecionar APK para upload"}
              </button>

              {uploadando && (
                <span className="text-muted small">
                  <span className="spinner-border spinner-border-sm me-1" style={{ width: 12, height: 12, borderWidth: 2 }} />
                  Aguarde, enviando arquivo...
                </span>
              )}

              {uploadInfo && !uploadando && (
                <span className="badge bg-success" style={{ fontSize: 12 }}>
                  <CheckCircle size={12} className="me-1" />
                  {(uploadInfo.tamanho_bytes / 1024 / 1024).toFixed(1)} MB enviado
                </span>
              )}
            </div>

            {uploadInfo && (
              <div className="alert alert-info mt-3 mb-0 py-2" style={{ fontSize: 12 }}>
                <strong>URL do APK:</strong>{" "}
                <a href={uploadInfo.url_apk} target="_blank" rel="noreferrer">{uploadInfo.url_apk}</a>
              </div>
            )}
          </div>
        </div>

        {/* Políticas por plataforma */}
        {loading ? (
          <p className="text-muted">Carregando políticas...</p>
        ) : (
          <div className="row g-4">
            {politicas.map((politica) => (
              <div className="col-12 col-lg-6" key={politica.plataforma}>
                <div className="card h-100" style={{ border: "1px solid #e2e8f0" }}>
                  <div className="card-header d-flex justify-content-between align-items-center" style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 20 }}>{politica.plataforma === "android" ? "🤖" : "🍎"}</span>
                      <strong style={{ textTransform: "capitalize" }}>{politica.plataforma}</strong>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span
                        className={`badge ${politica.ativo === "S" ? "bg-success" : "bg-secondary"}`}
                        style={{ fontSize: 11 }}
                      >
                        {politica.ativo === "S" ? "Ativo" : "Inativo"}
                      </span>
                      <div className="form-check form-switch mb-0">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          id={`ativo-${politica.plataforma}`}
                          checked={politica.ativo === "S"}
                          onChange={(e) =>
                            atualizarCampo(politica.plataforma, "ativo", e.target.checked ? "S" : "N")
                          }
                        />
                      </div>
                    </div>
                  </div>

                  <div className="card-body" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                    <div>
                      <label className="form-label fw-semibold" style={{ fontSize: 13 }}>
                        Build mínimo exigido
                        <span className="text-muted fw-normal ms-1" style={{ fontSize: 11 }}>
                          (número inteiro — ex: 2)
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control form-control-sm"
                        min={1}
                        value={politica.min_build || ""}
                        onChange={(e) => atualizarCampo(politica.plataforma, "min_build", e.target.value)}
                        placeholder="Ex: 2"
                      />
                      <div className="form-text">
                        Dispositivos com build inferior a este valor verão a tela de atualização obrigatória.
                      </div>
                    </div>

                    <div>
                      <label className="form-label fw-semibold" style={{ fontSize: 13 }}>
                        URL de download / store
                      </label>
                      <input
                        type="url"
                        className="form-control form-control-sm"
                        value={politica.store_url || ""}
                        onChange={(e) => atualizarCampo(politica.plataforma, "store_url", e.target.value)}
                        placeholder="https://..."
                      />
                      <div className="form-text">
                        Link que aparece no botão "Baixar nova versão" dentro do app.
                        {politica.plataforma === "android" && (
                          <> Para Android, o upload preenche este campo automaticamente com a URL do arquivo hospedado no servidor.</>                        )}
                      </div>
                    </div>

                    <div>
                      <label className="form-label fw-semibold" style={{ fontSize: 13 }}>
                        Mensagem exibida no app
                      </label>
                      <textarea
                        className="form-control form-control-sm"
                        rows={3}
                        value={politica.mensagem || ""}
                        onChange={(e) => atualizarCampo(politica.plataforma, "mensagem", e.target.value)}
                        placeholder="Ex: Nova versão disponível com melhorias no GPS..."
                      />
                    </div>
                  </div>

                  <div className="card-footer d-flex justify-content-end" style={{ background: "#f8fafc" }}>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      disabled={salvando === politica.plataforma}
                      onClick={() => salvar(politica)}
                      style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
                    >
                      {salvando === politica.plataforma ? (
                        <>
                          <span className="spinner-border spinner-border-sm" style={{ width: 12, height: 12, borderWidth: 2 }} />
                          Salvando...
                        </>
                      ) : (
                        <>
                          <Save size={14} strokeWidth={2} />
                          Salvar {politica.plataforma}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Instruções */}
        <div className="card mt-4" style={{ border: "1px solid #e2e8f0", background: "#f8fafc" }}>
          <div className="card-body py-3">
            <h6 className="fw-bold mb-2">Como funciona</h6>
            <ol className="mb-0 small text-muted" style={{ paddingLeft: 18 }}>
              <li>Incremente o <code>versionCode</code> no <code>app.json</code> (ex: <code>2</code> → <code>3</code>) e gere o APK com <code>JAVA_HOME=/usr/lib/jvm/java-1.17.0-openjdk-amd64 ./gradlew assembleRelease</code></li>
              <li>Clique em <strong>Selecionar APK para upload</strong>, informe o <strong>Build mínimo</strong> (deve ser igual ao novo versionCode) e confirme</li>
              <li>O servidor salva o APK, disponibiliza em <code>/download/app</code> e atualiza a política Android automaticamente com <strong>Ativo = Sim</strong></li>
              <li>No próximo heartbeat (≈10 segundos), dispositivos com build inferior verão a tela de atualização obrigatória com o link de download</li>
            </ol>
          </div>
        </div>
      </div>
    </>
  );
}
